import { createFileRoute, notFound } from "@tanstack/react-router";
import { listPublishedReports, type ReportRow } from "@/lib/reports.functions";
import { findReportBySlug, reportSlugMap } from "@/lib/report-slug";
import { absUrl, hreflangLinks } from "@/lib/seo";
import { pageTitle } from "@/lib/brand";
import { PageIntro, PageSection, Prose, MoreLink, LinkRows } from "@/components/site/page";

const CATEGORY_LABELS: Record<string, string> = {
  ai: "Yapay Zekâ",
  climate: "İklim",
  energy: "Enerji",
  sustainability: "Sürdürülebilirlik",
  biodiversity: "Biyoçeşitlilik",
  economy: "Ekonomi",
  "turkiye-politika": "Türkiye Politika",

  society: "Toplum",
  other: "Diğer",
};
const TYPE_LABELS: Record<string, string> = {
  report: "Rapor",
  index: "Endeks",
  dataset: "Veri Seti",
  framework: "Çerçeve",
};

function clamp(text: string, max = 155): string {
  const t = text.replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1).replace(/[\s,;:.]+\S*$/, "")}…`;
}

export const Route = createFileRoute("/raporlar/$slug")({
  loader: async ({ params, context }) => {
    const reports = await context.queryClient.ensureQueryData({
      queryKey: ["public-reports"],
      queryFn: () => listPublishedReports(),
    });
    const list = (reports ?? []) as ReportRow[];
    const report = findReportBySlug(list, params.slug);
    if (!report) throw notFound();
    const slugs = reportSlugMap(list);
    const related = list
      .filter((r) => r.id !== report.id && r.category === report.category)
      .slice(0, 4)
      .map((r) => ({
        title: r.title,
        organization: r.organization,
        year: r.year,
        slug: slugs.get(r.id)!,
      }));
    return { report, slug: slugs.get(report.id)!, related };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: pageTitle("Rapor bulunamadı", "tr") },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { report, slug } = loaderData;
    const url = absUrl(`/raporlar/${slug}`);
    const org = report.organization ? `${report.organization}` : "Rapor";
    const title = pageTitle(
      `${report.title}${report.year ? ` (${report.year})` : ""} — ${org}`,
      "tr",
    );
    const description = clamp(
      report.summary_tr ||
        `${org} tarafından yayımlanan "${report.title}" raporunun Türkçe özeti ve kaynak bağlantısı.`,
    );
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "tr_TR" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks(`/raporlar/${slug}`, `/research/${slug}`)],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Report",
                name: report.title,
                url,
                inLanguage: report.language || "en",
                ...(report.year ? { datePublished: String(report.year) } : {}),
                ...(report.organization
                  ? { publisher: { "@type": "Organization", name: report.organization } }
                  : {}),
                ...(report.summary_tr ? { abstract: report.summary_tr } : {}),
                ...(report.topics?.length ? { keywords: report.topics.join(", ") } : {}),
                sameAs: report.url,
                isAccessibleForFree: true,
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: absUrl("/") },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: "Rapor Kütüphanesi",
                    item: absUrl("/raporlar"),
                  },
                  { "@type": "ListItem", position: 3, name: report.title, item: url },
                ],
              },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div>
      <PageIntro crumbs={[{ label: "Rapor Kütüphanesi", to: "/raporlar" }, { label: "Bulunamadı" }]} title="Rapor bulunamadı" />
      <PageSection>
        <MoreLink to="/raporlar">Rapor Kütüphanesi</MoreLink>
      </PageSection>
    </div>
  ),
  component: ReportDetail,
});

function ReportDetail() {
  const { report, related } = Route.useLoaderData();
  const summary = report.summary_tr || report.summary_en;

  return (
    <div>
      <PageIntro
        crumbs={[{ label: "Rapor Kütüphanesi", to: "/raporlar" }, { label: report.title }]}
        kicker={
          <span className="flex flex-wrap items-center gap-2">
            <span>{CATEGORY_LABELS[report.category] ?? report.category}</span>
            <span className="rounded-full border border-ink/15 px-2 py-0.5 normal-case tracking-normal">{TYPE_LABELS[report.type] ?? report.type}</span>
            {report.year && <span>{report.year}</span>}
          </span>
        }
        title={report.title}
        lede={report.organization}
      />

      <PageSection title="Türkçe özet">
        <Prose>
          {(summary || "Bu rapor için özet henüz hazırlanmadı.")
            .split(/\n{2,}/)
            .map((p, i) => (
              <p key={i}>{p}</p>
            ))}
        </Prose>

        {report.topics && report.topics.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-2">
            {report.topics.map((t) => (
              <span key={t} className="inline-flex min-h-8 items-center rounded-full border border-ink/20 px-3 text-xs text-ink/75">
                {t}
              </span>
            ))}
          </div>
        )}

        <div className="mt-8">
          <MoreLink href={report.url}>Kaynağa git</MoreLink>
          <p className="mt-3 max-w-[60ch] text-xs leading-relaxed text-ink/55">
            Özet bu sitede hazırlanmıştır; rapor metninin telif hakkı yayımlayan kuruma aittir.
          </p>
        </div>
      </PageSection>

      {related.length > 0 && (
        <PageSection title="Aynı alanda diğer raporlar">
          <LinkRows
            withExcerpt={false}
            items={related.map((r) => ({
              key: r.slug,
              to: "/raporlar/$slug",
              params: { slug: r.slug },
              title: r.title,
              meta: r.organization ? `${r.organization}${r.year ? ` · ${r.year}` : ""}` : r.year ? String(r.year) : undefined,
            }))}
          />
        </PageSection>
      )}

      <PageSection>
        <MoreLink to="/raporlar">Rapor Kütüphanesi</MoreLink>
      </PageSection>
    </div>
  );
}
